
import { API_BASE_URL } from '../config';

const getAuthHeaders = () => {
    const token = localStorage.getItem("token")
    return {
        "Content-Type": "application/json",
        "Accept": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
    }
}

const handleResponse = async (response) => {
    const data = await response.json()

    if (!response.ok) {
        const error = new Error(data.message || "Bir hata oluştu")
        error.response = { data }
        throw error
    }

    return data
}

// Unified API object for easier usage
export const api = {
    get: async (endpoint) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            headers: getAuthHeaders(),
        })
        return handleResponse(response)
    },

    post: async (endpoint, data) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        return handleResponse(response)
    },

    put: async (endpoint, data) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        return handleResponse(response)
    },

    patch: async (endpoint, data) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "PATCH",
            headers: getAuthHeaders(),
            body: JSON.stringify(data),
        })
        return handleResponse(response)
    },

    delete: async (endpoint) => {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        })
        return handleResponse(response)
    },
}

// Auth API
export const authAPI = {
    login: async (phone, password) => {
        return api.post("/login", { phone, password })
    },

    fastLogin: async (data) => {
        // data: { name, surname, phone }
        return api.post("/fast-login", data)
    },

    register: async (userData) => {
        return api.post("/register", userData)
    },

    logout: async () => {
        return api.post("/logout")
    },
}

// Appointments API
export const appointmentsAPI = {
    getAll: async () => {
        return api.get("/appointments")
    },
    checkAvailability: async (staffId, date, serviceId, opt1 = null, opt2 = null) => {
        const params = new URLSearchParams();
        params.append('staff_id', staffId);
        params.append('date', date);         // Format: YYYY-MM-DD olmalı
        params.append('service_id', serviceId);

        if (opt1) params.append('optional_service_id_1', opt1);
        if (opt2) params.append('optional_service_id_2', opt2);

        // Yeni oluşturduğumuz rotaya istek atıyoruz
        return api.get(`/appointments/check-availability?${params.toString()}`);
    },
    getById: async (id) => {
        return api.get(`/appointments/${id}`)
    },
    getMyAppointments: async () => {
        return api.get("/appointments/my")
    },
    create: async (appointmentData) => {
        // appointmentData içinde optional_service_id_1, optional_service_id_2 ve price gönderilebilir
        return api.post("/appointments", appointmentData)
    },
    update: async (id, appointmentData) => {
        return api.put(`/appointments/${id}`, appointmentData)
    },
    delete: async (id) => {
        return api.delete(`/appointments/${id}`)
    },
    updateStatus: async (id, status) => {
        return api.patch(`/appointments/${id}/status`, { status })
    },
    getByUser: async (userId) => {
        return api.get(`/appointments/user/${userId}`)
    },
    cancel: async (id) => {
        return api.patch(`/appointments/${id}/status`, { status: "cancelled" })
    },
    getByStaff: async (staffId) => {
        return api.get(`/appointments/staff/${staffId}`)
    },

    getPendingCountForStaff: async () => {
        return api.get("/staff/me/appointments/pending-count");
    },
    /**
     * Giriş yapmış personelin bugünkü randevu sayısını getirir.
     */
    getTodayCountForStaff: async () => {
        return api.get("/staff/me/appointments/today-count");
    },

}
export const getMyUpcomingAppointmentsFromAPI = async () => {
    try {
        const response = await api.get('/appointments/my');
        if (response.data.success) {
            return response.data.appointments;
        }
        return [];
    } catch (error) {
        console.error('Randevular getirilirken hata:', error);
        throw error;
    }
};

// Services API
export const servicesAPI = {
    getAll: async () => {
        return api.get("/services")
    },
    getById: async (id) => {
        return api.get(`/services/${id}`)
    },
    create: async (serviceData) => {
        return api.post("/services", serviceData)
    },
    update: async (id, serviceData) => {
        return api.put(`/services/${id}`, serviceData)
    },
    delete: async (id) => {
        return api.delete(`/services/${id}`)
    },
}

// Users API
export const usersAPI = {
    getAll: async () => {
        return api.get("/users")
    },
    getById: async (id) => {
        return api.get(`/users/${id}`)
    },
    getCustomers: async () => {
        return api.get("/customers")
    },
    getStaff: async () => {
        return api.get("/staff")
    },
    create: async (userData) => {
        // Bu fonksiyon muhtemelen genel kullanıcı oluşturma içindir,
        // staff eklemek için createStaff'i kullanacağız.
        return api.post("/users", userData)
    },
    createStaff: async (staffData) => {
        // Admin'in personel eklemesi için bu fonksiyonu kullanacağız.
        return api.post("/staff", staffData)
    },
    update: async (id, userData) => {
        return api.put(`/users/${id}`, userData)
    },
    delete: async (id) => {
        return api.delete(`/users/${id}`)
    },
    storeFcmToken: async (token, deviceName = null) => {
        return api.post("/fcm-token", { token: token, device_name: deviceName });
    },
    removeFcmToken: async (token) => {
        // Backend'imiz token'ı body'de bekliyor, bu yüzden 'data' objesi gönderiyoruz
        return api.post("/fcm-token/delete", {token: token});
        // Not: Önceki cevaptaki 'api.delete' body göndermede sorun çıkarabilir.
        // Backend rotasını da '/fcm-token/delete' olarak POST yapmak daha güvenli olabilir.
        // Şimdilik UserController'daki delete methodunu POST olarak değiştirelim:
        // routes/api.php: Route::post('/fcm-token/delete', [UserController::class, 'removeFcmToken']);
        // UserController::removeFcmToken: public function removeFcmToken(Request $request) { ... } (Zaten böyle)
    },

    assignServices: async (staffId, serviceIds) => {
        // serviceIds: [1, 3, 5] gibi bir array olmalı
        return api.post(`/staff/${staffId}/assign-services`, { service_ids: serviceIds })
    }
}


export const remindersAPI = {
    /**
     * Giriş yapmış kullanıcının hatırlatıcılarını getirir.
     * @param {string} startDate (Opsiyonel) 'YYYY-MM-DD'
     * @param {string} endDate (Opsiyonel) 'YYYY-MM-DD'
     */
    getAll: async (startDate = null, endDate = null) => {
        const params = new URLSearchParams()
        if (startDate) params.append("start_date", startDate)
        if (endDate) params.append("end_date", endDate)

        const queryString = params.toString()
        return api.get(`/reminders${queryString ? `?${queryString}` : ""}`)
    },

    /**
     * Yeni hatırlatıcı oluşturur.
     * @param {object} reminderData { title, description, due_date }
     */
    create: async (reminderData) => {
        return api.post("/reminders", reminderData)
    },

    /**
     * Hatırlatıcıyı günceller.
     * @param {string|number} id
     * @param {object} reminderData { title, description, due_date }
     */
    update: async (id, reminderData) => {
        return api.put(`/reminders/${id}`, reminderData)
    },

    /**
     * Hatırlatıcıyı 'tamamlandı' olarak işaretler.
     * @param {string|number} id
     */
    markAsCompleted: async (id) => {
        return api.patch(`/reminders/${id}/complete`)
    },

    /**
     * Hatırlatıcıyı siler.
     * @param {string|number} id
     */
    delete: async (id) => {
        return api.delete(`/reminders/${id}`)
    },
}

// Ratings API
export const ratingsAPI = {
    getAll: async () => {
        return api.get("/ratings")
    },
    getById: async (id) => {
        return api.get(`/ratings/${id}`)
    },
    getByStaff: async (staffId) => {
        return api.get(`/staff/${staffId}/ratings`)
    },
    create: async (ratingData) => {
        return api.post("/ratings", ratingData)
    },
    update: async (id, ratingData) => {
        return api.put(`/ratings/${id}`, ratingData)
    },
    delete: async (id) => {
        return api.delete(`/ratings/${id}`)
    },
}


// Work Schedule API
export const workScheduleAPI = {
    getByStaff: async (staffId) => {
        return api.get(`/work-schedules/staff/${staffId}`)
    },
    getAvailableSlots: async (staffId, startDate = null, endDate = null) => {
        let url = `/staff/${staffId}/available-slots`
        const params = new URLSearchParams()

        if (startDate) {
            params.append("start_date", startDate)
        }
        if (endDate) {
            params.append("end_date", endDate)
        }

        if (params.toString()) {
            url += `?${params.toString()}`
        }

        return api.get(url)
    },
    generateSlots: async (slotData) => {
        return api.post("/work-slots/generate", slotData)
    },
    toggleSlot: async (slotId) => {
        return api.patch(`/work-hours/${slotId}/toggle`)
    },
    bulkToggleSlots: async (toggleData) => {
        return api.post("/work-slots/bulk-toggle", toggleData)
    },
    getWorkDays: async (staffId) => {
        return api.get(`/staff/${staffId}/work-days`)
    },
    getWorkHours: async (staffId) => {
        return api.get(`/staff/${staffId}/work-hours`)
    },
    create: async (scheduleData) => {
        return api.post("/work-schedules", scheduleData)
    },
    update: async (id, scheduleData) => {
        return api.put(`/work-schedules/${id}`, scheduleData)
    },
    delete: async (id) => {
        return api.delete(`/work-schedules/${id}`)
    },
    generateWorkHours: async (workDayId, weeksAhead = 4) => {
        return api.post("/work-slots/generate", {
            work_day_id: workDayId,
            weeks_ahead: weeksAhead
        })
    }

}
export const transactionsAPI = {
    /**
     * Tüm işlemleri filtreleyerek getirir.
     * @param {object} filters - { start_date, end_date, type, staff_id }
     */
    getAll: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);
        if (filters.type) params.append("type", filters.type);
        if (filters.staff_id) params.append("staff_id", filters.staff_id);

        // YENİ EKLENENLER:
        if (filters.payment_method) params.append("payment_method", filters.payment_method);
        if (filters.bank_account_id) params.append("bank_account_id", filters.bank_account_id);

        const queryString = params.toString();
        return api.get(`/transactions${queryString ? `?${queryString}` : ""}`);
    },

    /**
     * Finansal özeti (gelir, gider, net) filtreleyerek getirir.
     */
    getSummary: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.start_date) params.append("start_date", filters.start_date);
        if (filters.end_date) params.append("end_date", filters.end_date);
        if (filters.staff_id) params.append("staff_id", filters.staff_id);

        // YENİ EKLENENLER (Sorunu Çözen Kısım):
        if (filters.payment_method) params.append("payment_method", filters.payment_method);
        if (filters.bank_account_id) params.append("bank_account_id", filters.bank_account_id);

        const queryString = params.toString();
        return api.get(`/transactions/summary${queryString ? `?${queryString}` : ""}`);
    },

    getById: async (id) => {
        return api.get(`/transactions/${id}`);
    },

    create: async (transactionData) => {
        return api.post("/transactions", transactionData);
    },

    update: async (id, transactionData) => {
        return api.put(`/transactions/${id}`, transactionData);
    },

    delete: async (id) => {
        return api.delete(`/transactions/${id}`);
    },
};

// --- YENİ EKLENEN BLOK ---
// Products & Stock API
export const productsAPI = {
    // === Temel CRUD ===
    // GET /products
    getAll: async () => {
        return api.get("/products");
    },
    // GET /products/{id}
    getById: async (id) => {
        return api.get(`/products/${id}`);
    },
    // POST /products
    create: async (productData) => {
        // productData, 'initial_quantity' içermeli
        return api.post("/products", productData);
    },
    // PUT /products/{id}
    update: async (id, productData) => {
        return api.put(`/products/${id}`, productData);
    },
    // DELETE /products/{id}
    delete: async (id) => {
        return api.delete(`/products/${id}`);
    },

    // === Özel Stok İşlemleri ===
    // POST /products/{id}/add-stock
    addStock: async (id, quantity) => {
        return api.post(`/products/${id}/add-stock`, { quantity_added: quantity });
    },

    // POST /products/{id}/sell (GÜNCELLENDİ: Prim için staff_id eklendi)
    /**
     * Ürün satışı yapar.
     * @param {string|number} id - Ürün ID'si
     * @param {number} quantity - Satılan miktar
     * @param {string|number} staffId - (Opsiyonel) Satışı yapan ve prim alacak personel ID'si
     */
    sellStock: async (id, quantity, staffId = null) => {
        return api.post(`/products/${id}/sell`, {
            quantity_sold: quantity,
            staff_id: staffId // Prim alacak personelin ID'si
        });
    },

    // === Raporlama ===
    // GET /product-sales
    getSalesHistory: async () => {
        return api.get("/product-sales");
    },
    // GET /products/popular
    getPopular: async () => {
        return api.get("/products/popular");
    },
    // GET /products/low-stock
    getLowStock: async () => {
        return api.get("/products/low-stock");
    },
};

export const staffSalaryAPI = {
    /**
     * Bir personelin maaş ve prim ayarlarını getirir.
     * @param {string|number} staffId
     */
    getSettings: async (staffId) => {
        return api.get(`/staff-salary/${staffId}`);
    },

    /**
     * Bir personelin maaş ve prim ayarlarını günceller.
     * @param {string|number} staffId
     * @param {object} settingsData { base_salary, commission_rate_appointment, commission_rate_product }
     */
    updateSettings: async (staffId, settingsData) => {
        return api.post(`/staff-salary/${staffId}`, settingsData);
    },
};

export const checkoutAPI = {
    /**
     * Sepetteki ürünleri, hizmetleri ve randevuları işleyerek satışı tamamlar.
     * @param {object} payload - { customer_id, items: [...] }
     */
    process: async (payload) => {
        return api.post("/checkout/process", payload)
    },
}

export const bankAccountsAPI = {
    getAll: async () => {
        return api.get("/bank-accounts")
    },
    create: async (accountData) => {
        // accountData: { name, iban, fee_percentage }
        return api.post("/bank-accounts", accountData)
    },
    update: async (id, accountData) => {
        return api.put(`/bank-accounts/${id}`, accountData)
    },
    delete: async (id) => {
        return api.delete(`/bank-accounts/${id}`)
    },
}
